import { useState } from 'react';
import {
  AVATAR_ACCESSORIES,
  AVATAR_HAIR_COLORS,
  AVATAR_HAIR_STYLES,
  AVATAR_OUTFIT_COLORS,
  AVATAR_SKIN_TONES,
  type AvatarConfig,
} from '@etown/shared';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { HAIR_HEX, OUTFIT_HEX, SKIN_HEX, resolveAvatar } from '../../lib/avatarParts';
import { api } from '../../net/api';
import { useSessionStore } from '../../stores/sessionStore';
import { useOffice } from '../office/OfficeContext';
import { AvatarPreview } from './AvatarPreview';
import './profile.css';

interface AvatarEditorModalProps {
  onClose: () => void;
  /** Nach erfolgreichem Speichern (z. B. runtime.setSelfAvatar). */
  onSaved: (avatar: AvatarConfig) => void;
  /** Fallback-Farbindex fuer Benutzer ohne gespeicherte Konfiguration. */
  fallbackColor: number;
}

const HAIR_LABELS: Record<(typeof AVATAR_HAIR_STYLES)[number], string> = {
  glatze: 'Glatze',
  kurz: 'Kurz',
  bob: 'Bob',
  dutt: 'Dutt',
  locken: 'Locken',
  irokese: 'Irokese',
};

const ACCESSORY_LABELS: Record<(typeof AVATAR_ACCESSORIES)[number], string> = {
  keins: 'Keins',
  brille: 'Brille',
  sonnenbrille: 'Sonnenbrille',
  hut: 'Hut',
  muetze: 'Mütze',
  kopfhoerer: 'Kopfhörer',
};

function toCss(hex: number): string {
  return `#${hex.toString(16).padStart(6, '0')}`;
}

/** Avatar-Baukasten: Hautton, Frisur, Haarfarbe, Kleidung, Accessoire. */
export function AvatarEditorModal({ onClose, onSaved, fallbackColor }: AvatarEditorModalProps) {
  const { runtime } = useOffice();
  const user = useSessionStore((s) => s.user);
  const setSessionAvatar = useSessionStore((s) => s.setAvatar);
  const [config, setConfig] = useState<AvatarConfig>(() => resolveAvatar(user?.avatar ?? null, fallbackColor));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patch = (partial: Partial<AvatarConfig>): void => setConfig((c) => ({ ...c, ...partial }));

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await api.saveAvatar(config);
      setSessionAvatar(config);
      onSaved(config);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Avatar anpassen"
      onClose={onClose}
      wide
      footer={
        <>
          {error && <span className="avatar-editor__error" role="alert">{error}</span>}
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </>
      }
    >
      <div className="avatar-editor">
        <div className="avatar-editor__preview">
          <AvatarPreview config={config} snapshot={(c) => runtime.snapshotAvatar(c)} />
          <p className="avatar-editor__hint">So sehen dich die anderen im Büro.</p>
        </div>
        <div className="avatar-editor__controls">
          <fieldset className="avatar-editor__row">
            <legend>Hautton</legend>
            {AVATAR_SKIN_TONES.map((tone) => (
              <button
                key={tone}
                type="button"
                className={`avatar-editor__swatch ${config.skin === tone ? 'avatar-editor__swatch--active' : ''}`}
                style={{ background: toCss(SKIN_HEX[tone]) }}
                title={tone}
                aria-pressed={config.skin === tone}
                onClick={() => patch({ skin: tone })}
              />
            ))}
          </fieldset>
          <fieldset className="avatar-editor__row avatar-editor__row--wide">
            <legend>Frisur</legend>
            {AVATAR_HAIR_STYLES.map((style) => (
              <button
                key={style}
                type="button"
                className={`avatar-editor__chip ${config.hair === style ? 'avatar-editor__chip--active' : ''}`}
                aria-pressed={config.hair === style}
                onClick={() => patch({ hair: style })}
              >
                {HAIR_LABELS[style]}
              </button>
            ))}
          </fieldset>
          <fieldset className="avatar-editor__row">
            <legend>Haarfarbe</legend>
            {AVATAR_HAIR_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`avatar-editor__swatch ${config.hairColor === color ? 'avatar-editor__swatch--active' : ''}`}
                style={{ background: toCss(HAIR_HEX[color]) }}
                title={color}
                aria-pressed={config.hairColor === color}
                onClick={() => patch({ hairColor: color })}
              />
            ))}
          </fieldset>
          <fieldset className="avatar-editor__row">
            <legend>Oberteil</legend>
            {AVATAR_OUTFIT_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`avatar-editor__swatch ${config.shirtColor === color ? 'avatar-editor__swatch--active' : ''}`}
                style={{ background: toCss(OUTFIT_HEX[color]) }}
                title={color}
                aria-pressed={config.shirtColor === color}
                onClick={() => patch({ shirtColor: color })}
              />
            ))}
          </fieldset>
          <fieldset className="avatar-editor__row">
            <legend>Hose</legend>
            {AVATAR_OUTFIT_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`avatar-editor__swatch ${config.pantsColor === color ? 'avatar-editor__swatch--active' : ''}`}
                style={{ background: toCss(OUTFIT_HEX[color]) }}
                title={color}
                aria-pressed={config.pantsColor === color}
                onClick={() => patch({ pantsColor: color })}
              />
            ))}
          </fieldset>
          <fieldset className="avatar-editor__row avatar-editor__row--wide">
            <legend>Accessoire</legend>
            {AVATAR_ACCESSORIES.map((accessory) => (
              <button
                key={accessory}
                type="button"
                className={`avatar-editor__chip ${config.accessory === accessory ? 'avatar-editor__chip--active' : ''}`}
                aria-pressed={config.accessory === accessory}
                onClick={() => patch({ accessory })}
              >
                {ACCESSORY_LABELS[accessory]}
              </button>
            ))}
          </fieldset>
        </div>
      </div>
    </Modal>
  );
}
